import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition } from '../types';

// Standing still charges it, the first step lets it go; walking, it fires uncharged on each beat.
export interface ChargedAttack {
  kind: 'charged';
  damage: number;
  fullDamage: number;
  pierce: number;
  fullPierce: number;
  chargeTicks: number;
  cooldownTicks: number;
  projectileSpeed: number;
  projectileRadius: number;
  rangeTicks: number;
  gapRadians: number;
}

// A stride towards the aim that lands on a beat, striking and stunning around where it lands.
export interface GrandPasEffect {
  kind: 'grandPas';
  distance: number;
  leastTicks: number;
  damage: number;
  radius: number;
  stunBeats: number;
}

export const PLUNGE: ChargedAttack = {
  kind: 'charged',
  damage: 10,
  fullDamage: 60,
  pierce: 0,
  fullPierce: 4,
  chargeTicks: TICKS_PER_BEAT,
  cooldownTicks: (2 * TICKS_PER_BEAT) / 3,
  projectileSpeed: 22,
  projectileRadius: 8,
  rangeTicks: 20,
  gapRadians: 0.2,
};

export const ECHASSIER: ClassDefinition = {
  id: 'echassier',
  name: 'L’Échassier',
  role: 'Charge immobile un tir qui traverse la foule, bien au-dessus des petites bad vibes.',
  color: '#e85d75',
  maxHp: 120,
  speed: 7,
  radius: 14,
  pickupRadius: 90,
  towersOver: ['horde', 'rusher'],
  attack: PLUNGE,

  skill: {
    id: 'grand-pas',
    name: 'Grand pas',
    description: 'Une enjambée où tu vises, qui frappe et étourdit sur le temps à l’arrivée.',
    cooldownTicks: 3 * TICKS_PER_BAR,
    effect: {
      kind: 'grandPas',
      distance: 200,
      leastTicks: 4,
      damage: 30,
      radius: 70,
      stunBeats: 4,
    },
  },
  affinityWeaponIds: ['diabolo'],
};

export const ECHASSIER_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'boutons-dores',
    name: 'Boutons dorés',
    description: 'Ton tir chargé traverse une bad vibe de plus.',
    family: 'class',
    classId: 'echassier',
    modifiers: [{ stat: 'pierceAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Ton tir chargé traverse deux bad vibes de plus.',
        modifiers: [{ stat: 'pierceAdd', add: 2 }],
      },
      legendary: {
        description: 'Ton tir chargé traverse trois bad vibes de plus.',
        modifiers: [{ stat: 'pierceAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'double-fond',
    name: 'Double fond',
    description: 'Ton haut-de-forme lâche un tir de plus.',
    family: 'class',
    classId: 'echassier',
    modifiers: [{ stat: 'projectileCountAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Ton haut-de-forme lâche deux tirs de plus.',
        modifiers: [{ stat: 'projectileCountAdd', add: 2 }],
      },
      legendary: {
        description: 'Ton haut-de-forme lâche trois tirs de plus.',
        modifiers: [{ stat: 'projectileCountAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'pas-de-geant',
    name: 'Pas de géant',
    description: 'Ton grand pas frappe plus fort.',
    family: 'class',
    classId: 'echassier',
    modifiers: [{ stat: 'skillPowerMul', mul: 1.55 }],
    rarities: {
      rare: {
        description: 'Ton grand pas frappe deux fois plus fort.',
        modifiers: [{ stat: 'skillPowerMul', mul: 2.25 }],
      },
      legendary: {
        description: 'Ton grand pas frappe trois fois plus fort.',
        modifiers: [{ stat: 'skillPowerMul', mul: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'echasses-a-ressort',
    name: 'Échasses à ressort',
    description: 'Ton grand pas revient plus vite.',
    family: 'class',
    classId: 'echassier',
    modifiers: [{ stat: 'skillCooldownMul', mul: 0.64 }],
    rarities: {
      rare: {
        description: 'Ton grand pas revient bien plus vite.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.36 }],
      },
      legendary: {
        description: 'Ton grand pas revient presque aussitôt.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.16 }],
      },
    },
    maxStacks: 2,
  },
];
